import { AionCta } from "@/components/site/AionCta";
import { OG_IMAGES } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Rocket } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "House of X — Raj Shamani's D2C Venture Studio";
const DESC =
  "House of X is Raj Shamani's consumer venture studio. What it does, how it builds and backs Indian D2C brands, who it is for, and how it fits with his other businesses.";
const URL = "https://rajshamani.life/house-of-x";
const OG = OG_IMAGES.ventures;

const faq = [
  {
    q: "What is House of X?",
    a: "House of X is a consumer venture studio co-founded by Raj Shamani. Rather than only investing cash, it helps build and scale Indian direct-to-consumer brands by contributing distribution, content and operating support alongside capital.",
  },
  {
    q: "Who founded House of X?",
    a: "House of X was co-founded by Raj Shamani, who also hosts the Figuring Out podcast and is a director at Shamani Industries, his family's manufacturing business.",
  },
  {
    q: "What does a venture studio do differently from a VC fund?",
    a: "A venture studio is operationally involved. Instead of writing a cheque and taking a board seat, it works inside the business on brand, distribution, supply chain and audience — closer to a co-founder than an investor.",
  },
  {
    q: "What kind of brands does House of X work with?",
    a: "The focus is Indian consumer and D2C categories — food and beverage, personal care, wellness and lifestyle — where brand and distribution decide the winner more than technology does.",
  },
  {
    q: "How is House of X connected to Shamani Industries?",
    a: "They are separate businesses with complementary strengths. Shamani Industries is a manufacturing and B2B distribution business; House of X applies that supply-chain understanding to building consumer-facing brands.",
  },
];

export const Route = createFileRoute("/house-of-x")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "House of X venture studio", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Organization",
            "@id": `${URL}#house-of-x`,
            name: "House of X",
            description:
              "Consumer venture studio co-founded by Raj Shamani that builds and scales Indian D2C brands.",
            founder: {
              "@type": "Person",
              "@id": "https://rajshamani.life/about#person",
              name: "Raj Shamani",
            },
            areaServed: "IN",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: HouseOfXPage,
});

function HouseOfXPage() {
  return (
    <div>
      <PageBreadcrumb current="House of X" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Rocket size={14} /> Venture studio
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            <span className="text-gradient-gold">House of X</span> — Raj Shamani&apos;s D2C venture
            studio
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            House of X is the consumer venture studio co-founded by{" "}
            <Link to="/about" className="text-gold underline underline-offset-4">
              Raj Shamani
            </Link>
            . It builds and backs Indian direct-to-consumer brands with capital plus the two things
            most early brands lack: distribution and audience.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">What House of X actually does</h2>
        <div className="space-y-5 text-muted-foreground leading-relaxed max-w-3xl">
          <p>
            A venture studio sits between an accelerator and a co-founder. House of X does not just
            write a cheque and wait for quarterly updates — it works inside the brands it backs on
            positioning, packaging, supply chain, retail and content distribution.
          </p>
          <p>
            That model comes directly from Raj Shamani&apos;s own background. He grew up inside{" "}
            <Link to="/shamani-industries" className="text-gold underline underline-offset-4">
              Shamani Industries
            </Link>
            , a manufacturing and B2B distribution business, and then spent years building one of
            India&apos;s largest business audiences through the{" "}
            <Link to="/raj-shamani-podcast" className="text-gold underline underline-offset-4">
              Figuring Out podcast
            </Link>
            . House of X is the point where those two capabilities meet.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">The three things it contributes</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {[
            [
              "Distribution",
              "Access to modern trade, quick-commerce and general trade channels — the part where most Indian D2C brands stall after their first ₹1 crore online.",
            ],
            [
              "Audience",
              "Content and creator distribution that lets a new brand reach millions of business-minded Indians without buying every impression.",
            ],
            [
              "Operating support",
              "Supply chain, sourcing and unit-economics discipline carried over from a manufacturing background.",
            ],
          ].map(([title, body]) => (
            <div key={title} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">
          Where House of X fits in Raj Shamani&apos;s businesses
        </h2>
        <div className="space-y-5 text-muted-foreground leading-relaxed max-w-3xl">
          <p>
            Raj Shamani runs three connected things. Shamani Industries is the family manufacturing
            and distribution business he joined at 16. Figuring Out is the media property. House of X
            is the operating business that turns both into equity in consumer brands.
          </p>
          <p>
            Alongside the studio, he invests personally in early-stage Indian startups. The full list
            of companies and the thinking behind each cheque lives on the{" "}
            <Link to="/ventures" className="text-gold underline underline-offset-4">
              ventures and investments
            </Link>{" "}
            page, and the income mix is broken down on the{" "}
            <Link to="/raj-shamani-net-worth" className="text-gold underline underline-offset-4">
              net worth
            </Link>{" "}
            page.
          </p>
          <p>
            If you are building a D2C brand in India, the{" "}
            <Link
              to="/guides/d2c-brands-india"
              className="text-gold underline underline-offset-4"
            >
              D2C brands in India guide
            </Link>{" "}
            covers the same playbook in more practical detail.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/house-of-x" />

      <RelatedPages pages={["aion", "ventures", "about", "podcast", "contact", "faq"]} />
    </div>
  );
}
